/**
 * shared/fundDisplay.js — mọi nhãn hiển thị của một nguồn vốn đi qua đây.
 *
 * Trước đây Funding.jsx và FundDetail.jsx mỗi trang tự giữ một bảng nhãn riêng,
 * lệch với taxonomy (thiếu ANGEL_NETWORK, GOV, IMPACT_VC, PE_GROWTH), nên bộ lọc
 * hiện mã thô. Bảng nhãn giờ lấy từ FUND_TYPE trong @shared/taxonomy.js.
 */
import { label, isValid } from "./taxonomy.js";
import { toVnd } from "./money.js";

/**
 * Nhãn loại nguồn vốn cho người đọc.
 *
 * Loại TỔ CHỨC (type) và loại VỐN (capitalTypes) là hai chiều khác nhau. Một
 * đơn vị ghi type VC nhưng chỉ cấp vốn vay/hoàn trả (Beacon Fund, Jenfi) mà hiện
 * "Quỹ đầu tư mạo hiểm" sẽ khiến founder tưởng đó là vốn cổ phần. Vì vậy loại vốn
 * được ưu tiên khi nó không phải cổ phần.
 */
export function fundTypeKey(f) {
  const caps = f?.thesis?.capitalTypes || [];
  if (caps.length && caps.every((c) => c === "DEBT")) return "LENDER";
  if (caps.length && caps.every((c) => c === "GRANT")) return "GRANT";
  return f?.type || "";
}

export function fundTypeLabel(f) {
  return typeLabel(fundTypeKey(f));
}

/** Nhãn cho một mã FUND_TYPE. Không bao giờ trả về mã thô. */
export function typeLabel(code) {
  if (code && isValid("FUND_TYPE", code)) return label("FUND_TYPE", code);
  if (code && typeof console !== "undefined") {
    console.warn(`[fundLabels] Loại nguồn vốn chưa có nhãn: ${code}`);
  }
  return "Khác";
}

/** Nhóm loại vốn dùng cho bộ lọc "Loại vốn". */
export const CAPITAL_FILTERS = [
  { value: "EQUITY", label: "Đầu tư đổi cổ phần" },
  { value: "DEBT", label: "Vay và vốn hoàn trả" },
  { value: "GRANT", label: "Tài trợ không hoàn lại" },
];

export function matchesCapital(f, value) {
  if (!value) return true;
  const caps = f?.thesis?.capitalTypes || [];
  return caps.includes(value) || caps.includes("BLENDED");
}

/* ------------------------------------------------------------------ */
/* Khoản vốn                                                            */
/* ------------------------------------------------------------------ */

function soGon(n) {
  const r = Math.round(n * 10) / 10;
  return String(r).replace(".", ",");
}

/** Phần số đã rút gọn, không kèm đơn vị tiền. 200000 → "200 nghìn". */
function rutGon(major) {
  const a = Math.abs(major);
  if (a >= 1e9) return `${soGon(major / 1e9)} tỷ`;
  if (a >= 1e6) return `${soGon(major / 1e6)} triệu`;
  if (a >= 1e3) return `${soGon(major / 1e3)} nghìn`;
  return soGon(major);
}

function major(m) {
  if (!m) return null;
  if (m.ccy === "VND") return m.amount;
  if (m.ccy === "USD") return m.amount / 100;
  try {
    return toVnd(m);
  } catch {
    return null;
  }
}

const DON_VI = { VND: "đồng", USD: "USD" };

/**
 * Khoản tiền dạng dễ quét: "200 nghìn – 1 triệu USD", "Từ 500 triệu đồng",
 * "Tối đa 1 triệu USD". Giữ tiền tệ gốc, không quy đổi.
 */
export function formatRange(min, max) {
  const lo = major(min);
  const hi = major(max);
  if (lo == null && hi == null) return "Chưa công bố";
  const ccyLo = min?.ccy;
  const ccyHi = max?.ccy;
  if (lo != null && hi != null) {
    if (ccyLo === ccyHi) return `${rutGon(lo)} – ${rutGon(hi)} ${DON_VI[ccyLo] || ccyLo}`;
    return `${rutGon(lo)} ${DON_VI[ccyLo] || ccyLo} – ${rutGon(hi)} ${DON_VI[ccyHi] || ccyHi}`;
  }
  if (lo != null) return `Từ ${rutGon(lo)} ${DON_VI[ccyLo] || ccyLo}`;
  return `Tối đa ${rutGon(hi)} ${DON_VI[ccyHi] || ccyHi}`;
}

export function ticketText(f) {
  const t = f?.thesis?.ticket;
  if (!t?.min && !t?.max) return "Chưa công bố";
  return formatRange(t.min, t.max);
}

/**
 * Nhãn cho khoảng tiền, theo ĐÚNG bản chất của con số: khoảng thương vụ đã quan
 * sát không phải cam kết của quỹ, hạn mức vay không phải tiền đầu tư cổ phần.
 */
export function ticketLabel(f) {
  const t = f?.thesis?.ticket;
  if (t?.basis === "OBSERVED_DEALS") return "Khoảng thương vụ đã công bố";
  if (f?.thesis?.capitalTypes?.includes("DEBT")) return "Hạn mức tài trợ tham khảo";
  if (f?.type === "ACCELERATOR" || f?.type === "PROGRAM") return "Khoản đầu tư của chương trình";
  return "Khoản đầu tư ban đầu";
}

/** Nhãn độ tin cậy của con số. Null khi quỹ tự công bố và tin cậy cao. */
export function ticketBadge(f) {
  const t = f?.thesis?.ticket;
  if (!t?.min && !t?.max) return null;
  if (t.sourceType === "OFFICIAL" && t.confidence === "HIGH") return null;
  if (t.sourceType === "OFFICIAL_STALE") return "Quỹ công bố đã lâu, cần xác nhận lại";
  if (t.basis === "OBSERVED_DEALS") return "Suy từ thương vụ đã công bố, không phải cam kết";
  if (t.sourceType === "PRESS") return "Theo báo chí, quỹ chưa tự công bố";
  return "Tham khảo, quỹ chưa công bố chính thức";
}

/* ------------------------------------------------------------------ */
/* Nút hành động                                                        */
/* ------------------------------------------------------------------ */

// Đường dẫn trông giống biểu mẫu nộp hồ sơ thật (không phải trang liên hệ chung).
const DAU_HIEU_NOP = /(apply|pitch|\/form|typeform|forms\.gle|docs\.google\.com\/forms|larksuite\.com\/share\/base\/form)/i;

/**
 * Nút chính trên thẻ nguồn vốn.
 *
 * Ghi rõ `process.ctaType: 'apply' | 'contact'` trong dữ liệu để quyết định.
 * Khi chưa ghi, chỉ gọi là "Nộp hồ sơ" nếu đường dẫn trông như biểu mẫu nộp;
 * mọi trường hợp khác là "Liên hệ" — nói ít hơn thực tế vẫn an toàn hơn hứa
 * một kênh nộp hồ sơ không tồn tại.
 */
export function fundCta(f) {
  const p = f?.process || {};
  const goc = (u) => String(u || "").toLowerCase().replace(/^https?:\/\/(www\.)?/, "").replace(/\/+$/, "");
  if (p.submitUrl) {
    const kind = p.ctaType === "apply" || p.ctaType === "contact"
      ? p.ctaType
      : DAU_HIEU_NOP.test(p.submitUrl) ? "apply" : "contact";
    // Đường dẫn "liên hệ" trùng trang chủ thì nút "Trang chính thức" đã đủ.
    if (kind === "contact" && f.website && goc(p.submitUrl) === goc(f.website)) {
      return p.submitEmail
        ? { href: `mailto:${p.submitEmail}`, kind: "email", label: "Gửi hồ sơ qua email", external: false }
        : null;
    }
    return {
      href: p.submitUrl,
      kind,
      label: kind === "apply" ? "Nộp hồ sơ ↗" : "Liên hệ ↗",
      external: true,
    };
  }
  if (p.submitEmail) {
    return {
      href: `mailto:${p.submitEmail}`,
      kind: "email",
      label: "Gửi hồ sơ qua email",
      external: false,
    };
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Danh mục đã đầu tư                                                   */
/* ------------------------------------------------------------------ */

/** Một khoản tiền đơn lẻ: "5 triệu USD". Giữ nguyên tiền tệ gốc. */
export function formatMoney(m) {
  const v = major(m);
  if (v == null) return "";
  return `${rutGon(v)} ${DON_VI[m.ccy] || m.ccy}`;
}

/**
 * Câu trả lời cho "quỹ muốn mình giống ai", dựng từ tomTatDanhMuc() nên luôn
 * khớp với danh sách hiện ngay bên dưới nó. Trả "" khi danh mục quá mỏng.
 */
function veKhuynhHuong(tomTat, coVong) {
  const ten = (kind, ids) => ids.map((id) => label(kind, id)).join(", ");
  const ve = [];
  if (tomTat.nganh.length) ve.push(`ngành xuất hiện nhiều nhất là ${ten("INDUSTRY", tomTat.nganh)}`);
  if (coVong && tomTat.vong.length) ve.push(`quỹ thường vào ở vòng ${ten("STAGE", tomTat.vong)}`);
  return ve;
}

export function tomTatDanhMucText(f, tomTat) {
  if (!tomTat) return "";
  const ve = veKhuynhHuong(tomTat, true);
  const dau = `${tomTat.soCty} doanh nghiệp được nêu công khai`
    + (tomTat.soVN && tomTat.soVN < tomTat.soCty ? `, ${tomTat.soVN} trong số đó ở Việt Nam` : "");
  if (!ve.length) return `${dau}, trải trên nhiều ngành khác nhau, chưa thấy một hướng nào nổi trội.`;
  return `${dau}: ${ve.join("; ")}.`;
}

/**
 * Câu tóm tắt cho chương trình. Khác câu của quỹ ở hai chỗ có thật: chương trình
 * KHÔNG rót vốn theo vòng nên không nói "vào ở vòng nào", và động từ không phải
 * "đã đầu tư".
 *
 * `cumTu` là cụm danh từ + động từ, do bên gọi truyền vào theo loại chương trình:
 * chương trình tăng tốc thì "doanh nghiệp đã qua chương trình", quỹ tài trợ thì
 * "dự án đã được tài trợ". Gọi sai chỗ này là hứa hộ người khác một điều họ
 * không hứa, nên nhãn phải đi theo dữ liệu chứ không để mặc định cho tất cả.
 */
export function tomTatAlumniText(tomTat, cumTu = "doanh nghiệp đã qua chương trình") {
  if (!tomTat) return "";
  const ve = veKhuynhHuong(tomTat, false);
  const dau = `${tomTat.soCty} ${cumTu}`
    + (tomTat.soVN && tomTat.soVN < tomTat.soCty ? `, ${tomTat.soVN} trong số đó ở Việt Nam` : "");
  if (!ve.length) return `${dau}, trải trên nhiều ngành khác nhau.`;
  return `${dau}: ${ve.join("; ")}.`;
}

/** Nhãn kết quả của một khoản đầu tư. */
export function ketQuaLabel(code) {
  return ({
    ACQUIRED: "Đã được mua lại",
    IPO: "Đã niêm yết",
    FOLLOW_ON: "Quỹ rót tiếp vòng sau",
    EXITED: "Quỹ đã thoái vốn",
  })[code] || "";
}

/** "5/2023", "2023" — theo đúng mức chi tiết mà công bố có. */
export function thoiDiemVong(s) {
  if (!s) return "";
  const [y, m] = String(s).split("-");
  return m ? `${Number(m)}/${y}` : y;
}

/* ------------------------------------------------------------------ */
/* Quỹ tương tự (cuối trang chi tiết)                                   */
/* ------------------------------------------------------------------ */

/**
 * Chọn tối đa `n` nguồn vốn gần nhất với `f`, chỉ dựa trên điều các đơn vị đã
 * công bố: chung giai đoạn (nặng nhất, vì founder tìm theo vòng), chung ngành,
 * cùng loại đơn vị, chung địa bàn. Điểm dưới 2 thì không đưa ra: thà ít gợi ý
 * còn hơn gợi ý một quỹ khác hẳn vòng.
 */
export function quyTuongTu(f, ds = [], n = 3) {
  const chung = (a, b) => (a || []).filter((x) => (b || []).includes(x)).length;
  const th = f?.thesis || {};
  return ds
    .filter((g) => g && g.fundId !== f?.fundId)
    .map((g) => {
      const t = g.thesis || {};
      const diem = 2 * chung(th.stages, t.stages)
        + Math.min(3, chung(th.industries, t.industries))
        + (fundTypeKey(g) === fundTypeKey(f) ? 1 : 0)
        + (chung(th.geos, t.geos) > 0 ? 0.5 : 0);
      return { g, diem };
    })
    .filter((x) => x.diem >= 2)
    .sort((a, b) => b.diem - a.diem
      || String(b.g.provenance?.lastVerifiedAt || "").localeCompare(String(a.g.provenance?.lastVerifiedAt || ""))
      || String(a.g.name).localeCompare(String(b.g.name)))
    .slice(0, n)
    .map((x) => x.g);
}
